import React, { useState, useEffect, useMemo } from "react";
import VoiceUsage from "./VoiceUsage";
import TokenInspector from "./TokenInspector";
import {
  FiActivity, FiClock, FiCpu, FiZap, FiMessageSquare, FiMic,
  FiAlertTriangle, FiCheckCircle, FiChevronLeft, FiChevronRight,
  FiCode, FiDollarSign, FiTrendingUp, FiRefreshCw, FiArrowUp,
  FiArrowDown, FiLayers, FiBarChart2
} from "react-icons/fi";
import "../assets/css/settings-pages.css";

const NEXT_API_URL = process.env.REACT_APP_NEXT_API_URL || "http://localhost:3000";
const PAGE_SIZE = 10;

const formatNumber = (num) => {
  if (num === null || num === undefined) return "-";
  if (num >= 1000000) return `${(num / 1000000).toFixed(1)}M`;
  if (num >= 1000) return `${(num / 1000).toFixed(1)}K`;
  return num.toLocaleString();
};

const formatCost = (cost) => {
  if (cost === null || cost === undefined || isNaN(cost)) return "-";
  if (cost === 0) return "$0.00";
  if (cost < 0.001) return `$${cost.toFixed(5)}`;
  if (cost < 0.01) return `$${cost.toFixed(4)}`;
  return `$${cost.toFixed(3)}`;
};

const formatDate = (dateStr) => {
  if (!dateStr) return "-";
  const date = new Date(dateStr);
  const now = new Date();
  const diff = now - date;
  if (diff < 60000) return "Just now";
  if (diff < 3600000) return `${Math.floor(diff / 60000)}m ago`;
  if (diff < 86400000) return `${Math.floor(diff / 3600000)}h ago`;
  return date.toLocaleDateString("en-US", { month: "short", day: "numeric", hour: "2-digit", minute: "2-digit" });
};

const getTierConfig = (tier) => {
  const configs = {
    full: { color: "teal", label: "Full", icon: FiLayers },
    standard: { color: "blue", label: "Standard", icon: FiBarChart2 },
    lite: { color: "orange", label: "Lite", icon: FiZap },
    minimal: { color: "gray", label: "Minimal", icon: FiActivity },
  };
  return configs[tier] || configs.minimal;
};

function Usage({ userId }) {
  const [activeTab, setActiveTab] = useState("chat");
  const [last24h, setLast24h] = useState(null);
  const [sessions, setSessions] = useState([]);
  const [selectedSession, setSelectedSession] = useState("");
  const [sessionStats, setSessionStats] = useState(null);
  const [recentUsage, setRecentUsage] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [currentPage, setCurrentPage] = useState(1);
  const [refreshing, setRefreshing] = useState(false);

  const fetchData = async (isRefresh = false) => {
    if (!userId) return;
    if (isRefresh) setRefreshing(true);
    else setLoading(true);
    setError(null);

    try {
      const [last24hRes, sessionsRes, recentRes] = await Promise.all([
        fetch(`${NEXT_API_URL}/api/usage/last24h?user_id=${userId}`),
        fetch(`${NEXT_API_URL}/api/usage/sessions?user_id=${userId}&limit=50`),
        fetch(`${NEXT_API_URL}/api/usage/recent?user_id=${userId}&limit=100`)
      ]);

      if (last24hRes.ok) {
        const d = await last24hRes.json();
        if (d.success) setLast24h(d.data);
      }
      if (sessionsRes.ok) {
        const d = await sessionsRes.json();
        if (d.success) setSessions(d.data || []);
      }
      if (recentRes.ok) {
        const d = await recentRes.json();
        if (d.success) setRecentUsage(d.data || []);
      }
    } catch (err) {
      console.error("Error fetching usage data:", err);
      setError(err.message || "Failed to fetch usage data");
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  };

  useEffect(() => {
    if (userId) fetchData();
  }, [userId]);

  useEffect(() => {
    if (!selectedSession || !userId) { setSessionStats(null); return; }
    const fetchSession = async () => {
      try {
        const res = await fetch(`${NEXT_API_URL}/api/usage/session?user_id=${userId}&session_id=${selectedSession}`);
        if (res.ok) { const d = await res.json(); if (d.success) setSessionStats(d.data); }
      } catch (err) { console.error("Error fetching session stats:", err); }
    };
    fetchSession();
  }, [selectedSession, userId]);

  const totalPages = Math.ceil(recentUsage.length / PAGE_SIZE);
  const paginatedUsage = useMemo(() => {
    const start = (currentPage - 1) * PAGE_SIZE;
    return recentUsage.slice(start, start + PAGE_SIZE);
  }, [recentUsage, currentPage]);

  useEffect(() => { setCurrentPage(1); }, [recentUsage]);

  // Derived stats
  const totalCost24h = useMemo(() => {
    return recentUsage.reduce((sum, r) => sum + (r.costUsd || 0), 0);
  }, [recentUsage]);

  const modelBreakdown = useMemo(() => {
    const map = {};
    recentUsage.forEach(r => {
      const model = r.model || "unknown";
      if (!map[model]) map[model] = { calls: 0, tokens: 0, cost: 0 };
      map[model].calls++;
      map[model].tokens += r.totalTokens || 0;
      map[model].cost += r.costUsd || 0;
    });
    return Object.entries(map)
      .sort((a, b) => b[1].calls - a[1].calls)
      .slice(0, 5);
  }, [recentUsage]);

  if (loading) {
    return (
      <div className="settings-page-container">
        <div className="usage-loading">
          <div className="usage-loading-spinner" />
          <p>Loading usage data...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="settings-page-container">
        <div className="usage-error-card">
          <FiAlertTriangle size={32} />
          <h4>Unable to load usage data</h4>
          <p>{error}</p>
          <button className="usage-btn usage-btn-primary" onClick={() => fetchData()}>
            <FiRefreshCw size={14} /> Try Again
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="settings-page-container">
      {/* Page Header */}
      <div className="usage-page-header">
        <div>
          <h2 className="usage-page-title">Usage Dashboard</h2>
          <p className="usage-page-subtitle">Monitor your usage, tokens, and costs</p>
        </div>
        <button
          className="usage-btn usage-btn-outline"
          onClick={() => fetchData(true)}
          disabled={refreshing}
        >
          <FiRefreshCw size={14} className={refreshing ? "usage-spin" : ""} />
          {refreshing ? "Refreshing..." : "Refresh"}
        </button>
      </div>

      {/* Hero Stats - Big numbers */}
      {last24h && (
        <div className="usage-hero-grid">
          <div className="usage-hero-card usage-hero-primary">
            <div className="usage-hero-icon">
              <FiDollarSign size={24} />
            </div>
            <div className="usage-hero-content">
              <span className="usage-hero-value">{formatCost(totalCost24h)}</span>
              <span className="usage-hero-label">Total Cost (24h)</span>
            </div>
          </div>
          <div className="usage-hero-card">
            <div className="usage-hero-icon usage-icon-teal">
              <FiActivity size={22} />
            </div>
            <div className="usage-hero-content">
              <span className="usage-hero-value">{formatNumber(last24h.callsCount)}</span>
              <span className="usage-hero-label">API Calls</span>
            </div>
          </div>
          <div className="usage-hero-card">
            <div className="usage-hero-icon usage-icon-orange">
              <FiZap size={22} />
            </div>
            <div className="usage-hero-content">
              <span className="usage-hero-value">{formatNumber(last24h.sumTotalTokens)}</span>
              <span className="usage-hero-label">Total Tokens</span>
            </div>
          </div>
          <div className="usage-hero-card">
            <div className="usage-hero-icon usage-icon-purple">
              <FiClock size={22} />
            </div>
            <div className="usage-hero-content">
              <span className="usage-hero-value">{last24h.avgLatencyMs ? `${last24h.avgLatencyMs}ms` : "-"}</span>
              <span className="usage-hero-label">Avg Latency</span>
            </div>
          </div>
        </div>
      )}

      {/* Tab Navigation */}
      <div className="usage-tabs">
        <button
          className={`usage-tab ${activeTab === "chat" ? "active" : ""}`}
          onClick={() => setActiveTab("chat")}
        >
          <FiMessageSquare size={16} />
          <span>Chat Usage</span>
        </button>
        <button
          className={`usage-tab ${activeTab === "voice" ? "active" : ""}`}
          onClick={() => setActiveTab("voice")}
        >
          <FiMic size={16} />
          <span>Voice Usage</span>
        </button>
        {process.env.REACT_APP_TOKEN_DEBUG === "true" && (
          <button
            className={`usage-tab ${activeTab === "inspector" ? "active" : ""}`}
            onClick={() => setActiveTab("inspector")}
          >
            <FiCode size={16} />
            <span>Token Inspector</span>
          </button>
        )}
      </div>

      {/* Voice Tab */}
      {activeTab === "voice" && <VoiceUsage userId={userId} />}

      {/* Token Inspector Tab */}
      {activeTab === "inspector" && process.env.REACT_APP_TOKEN_DEBUG === "true" && (
        <div className="settings-section-card">
          <TokenInspector userId={userId} />
        </div>
      )}

      {/* Chat Usage Tab */}
      {activeTab === "chat" && (
        <>
          {/* Token Breakdown + Model Breakdown side by side */}
          <div className="usage-two-col">
            {/* Token Breakdown */}
            {last24h && (
              <div className="usage-card">
                <div className="usage-card-header">
                  <FiTrendingUp size={18} className="usage-card-header-icon" />
                  <h5>Token Breakdown</h5>
                  <span className="usage-card-badge">24h</span>
                </div>
                <div className="usage-card-body">
                  <div className="usage-token-bars">
                    <div className="usage-token-row">
                      <div className="usage-token-info">
                        <FiArrowUp size={14} className="usage-token-icon-up" />
                        <span className="usage-token-name">Input Tokens</span>
                      </div>
                      <span className="usage-token-value">{formatNumber(last24h.sumPromptTokens)}</span>
                      <div className="usage-token-bar">
                        <div
                          className="usage-token-bar-fill usage-bar-teal"
                          style={{ width: `${last24h.sumTotalTokens ? (last24h.sumPromptTokens / last24h.sumTotalTokens * 100) : 0}%` }}
                        />
                      </div>
                    </div>
                    <div className="usage-token-row">
                      <div className="usage-token-info">
                        <FiArrowDown size={14} className="usage-token-icon-down" />
                        <span className="usage-token-name">Output Tokens</span>
                      </div>
                      <span className="usage-token-value">{formatNumber(last24h.sumCompletionTokens)}</span>
                      <div className="usage-token-bar">
                        <div
                          className="usage-token-bar-fill usage-bar-orange"
                          style={{ width: `${last24h.sumTotalTokens ? (last24h.sumCompletionTokens / last24h.sumTotalTokens * 100) : 0}%` }}
                        />
                      </div>
                    </div>
                  </div>
                  <div className="usage-token-footer">
                    <span>Top Tier: <strong>{last24h.topTierByCalls || "-"}</strong></span>
                    <span>Top Model: <strong style={{ fontSize: "12px" }}>{last24h.topModelByCalls || "-"}</strong></span>
                  </div>
                </div>
              </div>
            )}

            {/* Model Breakdown */}
            <div className="usage-card">
              <div className="usage-card-header">
                <FiCpu size={18} className="usage-card-header-icon" />
                <h5>Model Usage</h5>
                <span className="usage-card-badge">{modelBreakdown.length} models</span>
              </div>
              <div className="usage-card-body">
                {modelBreakdown.length === 0 ? (
                  <div className="usage-empty-mini">No model data yet</div>
                ) : (
                  <div className="usage-model-list">
                    {modelBreakdown.map(([model, stats]) => (
                      <div key={model} className="usage-model-item">
                        <div className="usage-model-name" title={model}>
                          {model.split("/").pop().split("-").slice(0, 3).join("-")}
                        </div>
                        <div className="usage-model-stats">
                          <span className="usage-model-calls">{stats.calls} calls</span>
                          <span className="usage-model-tokens">{formatNumber(stats.tokens)} tok</span>
                          <span className="usage-model-cost">{formatCost(stats.cost)}</span>
                        </div>
                        <div className="usage-model-bar">
                          <div
                            className="usage-model-bar-fill"
                            style={{ width: `${(stats.calls / modelBreakdown[0][1].calls) * 100}%` }}
                          />
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            </div>
          </div>

          {/* Topic Selector */}
          <div className="usage-card">
            <div className="usage-card-header">
              <FiMessageSquare size={18} className="usage-card-header-icon" />
              <h5>Topic Usage</h5>
              <span className="usage-card-badge">{sessions.length} topics</span>
            </div>
            <div className="usage-card-body">
              <select
                className="usage-select"
                value={selectedSession}
                onChange={(e) => setSelectedSession(e.target.value)}
              >
                <option value="">Select a topic to view usage...</option>
                {sessions.map((s) => (
                  <option key={s.id} value={s.id}>
                    {s.name || "Untitled"} -- {new Date(s.createdAt).toLocaleDateString("en-US", { month: "short", day: "numeric", hour: "2-digit", minute: "2-digit" })}
                  </option>
                ))}
              </select>

              {sessionStats && (
                <div className="usage-session-stats">
                  <div className="usage-session-stat">
                    <FiActivity size={16} className="usage-icon-teal" />
                    <div>
                      <strong>{formatNumber(sessionStats.callsCount)}</strong>
                      <span>Calls</span>
                    </div>
                  </div>
                  <div className="usage-session-stat">
                    <FiZap size={16} className="usage-icon-orange" />
                    <div>
                      <strong>{formatNumber(sessionStats.sumTotalTokens)}</strong>
                      <span>Tokens</span>
                    </div>
                  </div>
                  <div className="usage-session-stat">
                    <FiArrowUp size={16} className="usage-icon-teal" />
                    <div>
                      <strong>{formatNumber(sessionStats.sumPromptTokens)}</strong>
                      <span>Input</span>
                    </div>
                  </div>
                  <div className="usage-session-stat">
                    <FiArrowDown size={16} className="usage-icon-purple" />
                    <div>
                      <strong>{formatNumber(sessionStats.sumCompletionTokens)}</strong>
                      <span>Output</span>
                    </div>
                  </div>
                </div>
              )}
            </div>
          </div>

          {/* Recent API Calls */}
          <div className="usage-card">
            <div className="usage-card-header">
              <FiActivity size={18} className="usage-card-header-icon" />
              <h5>Recent API Calls</h5>
              <span className="usage-card-badge">{recentUsage.length} total</span>
            </div>
            <div className="usage-table-container">
              <table className="usage-table">
                <thead>
                  <tr>
                    <th>Time</th>
                    <th>Model</th>
                    <th>Tier</th>
                    <th>Input</th>
                    <th>Output</th>
                    <th>Total</th>
                    <th>Cost</th>
                    <th>Latency</th>
                    <th>Status</th>
                  </tr>
                </thead>
                <tbody>
                  {paginatedUsage.length === 0 ? (
                    <tr>
                      <td colSpan="9" className="usage-table-empty">
                        <FiActivity size={20} />
                        <span>No usage data yet</span>
                      </td>
                    </tr>
                  ) : (
                    paginatedUsage.map((row) => {
                      const tierConfig = getTierConfig(row.tier);
                      return (
                        <tr key={row.id}>
                          <td className="usage-td-time">{formatDate(row.createdAt)}</td>
                          <td className="usage-td-model" title={row.model}>{row.model?.split("/").pop()?.split("-").slice(0, 4).join("-") || "-"}</td>
                          <td>
                            <span className={`usage-tier-pill ${tierConfig.color}`}>{tierConfig.label}</span>
                          </td>
                          <td className="usage-td-num">{formatNumber(row.promptTokens)}</td>
                          <td className="usage-td-num">{formatNumber(row.completionTokens)}</td>
                          <td className="usage-td-num usage-td-total">{formatNumber(row.totalTokens)}</td>
                          <td className="usage-td-cost">{formatCost(row.costUsd)}</td>
                          <td className="usage-td-latency">{row.latencyMs ? `${row.latencyMs}ms` : "-"}</td>
                          <td className="usage-td-status">
                            {row.usageMissingReason ? (
                              <span className="usage-status-warn" title={row.usageMissingReason}><FiAlertTriangle size={14} /></span>
                            ) : (
                              <span className="usage-status-ok"><FiCheckCircle size={14} /></span>
                            )}
                          </td>
                        </tr>
                      );
                    })
                  )}
                </tbody>
              </table>
            </div>

            {/* Pagination */}
            {totalPages > 1 && (
              <div className="usage-pagination">
                <span className="usage-pagination-info">
                  {((currentPage - 1) * PAGE_SIZE) + 1}-{Math.min(currentPage * PAGE_SIZE, recentUsage.length)} of {recentUsage.length}
                </span>
                <div className="usage-pagination-btns">
                  <button
                    className="usage-page-btn"
                    onClick={() => setCurrentPage(p => Math.max(1, p - 1))}
                    disabled={currentPage === 1}
                  >
                    <FiChevronLeft size={16} />
                  </button>
                  {Array.from({ length: totalPages }, (_, i) => i + 1)
                    .filter(page => page === 1 || page === totalPages || Math.abs(page - currentPage) <= 1)
                    .reduce((acc, page, idx, arr) => {
                      if (idx > 0 && page - arr[idx - 1] > 1) {
                        acc.push(<span key={`e-${page}`} className="usage-page-dots">...</span>);
                      }
                      acc.push(
                        <button
                          key={page}
                          className={`usage-page-btn ${currentPage === page ? "active" : ""}`}
                          onClick={() => setCurrentPage(page)}
                        >
                          {page}
                        </button>
                      );
                      return acc;
                    }, [])}
                  <button
                    className="usage-page-btn"
                    onClick={() => setCurrentPage(p => Math.min(totalPages, p + 1))}
                    disabled={currentPage === totalPages}
                  >
                    <FiChevronRight size={16} />
                  </button>
                </div>
              </div>
            )}
          </div>
        </>
      )}
    </div>
  );
}

export default Usage;
